import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { EnvService } from './env.service';
import { Observable } from 'rxjs';
import { IAlert } from '../components/malla/interfaces/IAlertInfo';
import { IResponseGetMallaData } from '../components/malla/interfaces/IResponseGetMallaData';
import { IVersions } from '../components/malla/interfaces/IVersions';
import { INode } from '../components/malla/interfaces/INode';
import { IEdge } from '../components/malla/interfaces/IEdge';
import { FormatoTransferencias } from '../components/malla/helpers/transferenciasExcel';

export interface ErrorImportacion {
  fila: number | null;
  columna: string;
  valor: unknown;
  mensaje: string;
}

export interface ResultadoValidacion {
  ok: boolean;
  filas: number;
  errores: ErrorImportacion[];
  totalErrores: number;
  resumen: {
    transferencias: number;
    repetidas: number;
    cuentas: number;
    personas: number;
    montoTotal: number;
    desde: string | null;
    hasta: string | null;
  };
}

export interface ResultadoCarga {
  importadas: number;
  omitidas: number;
}

@Injectable({
  providedIn: 'root',
})
export class MallaService {
  url = `${this.envService.URL_MALLA}/${new Date()}/carga`;
  url_cuentas = `${this.envService.URL_MALLA}/${new Date()}/cuenta`;

  constructor(private http: HttpClient, private envService: EnvService) { }

  getMalla(caso_id: string): Observable<any> {
    return this.http.get<IResponseGetMallaData>(`${this.url}/${caso_id}`);
  }

  /** Formato «Transferencias»: columnas, reglas y bancos (fuente única: el back). */
  formato(): Observable<FormatoTransferencias> {
    return this.http.get<FormatoTransferencias>(`${this.url}/formato`);
  }

  private archivo(file: File, caso_id: string, numero_de_identificacion: string) {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('caso_id', caso_id);
    formData.append('numero_de_identificacion', numero_de_identificacion || '');
    return formData;
  }

  /** Valida el Excel sin guardar nada. */
  validarCarga(file: File, caso_id: string, numero_de_identificacion: string): Observable<ResultadoValidacion> {
    return this.http.post<ResultadoValidacion>(`${this.url}/validar`, this.archivo(file, caso_id, numero_de_identificacion));
  }

  cargaMasiva(file: File, caso_id: string, numero_de_identificacion: string): Observable<ResultadoCarga> {
    return this.http.post<ResultadoCarga>(this.url, this.archivo(file, caso_id, numero_de_identificacion));
  }

  export(caso_id: string): Observable<any> {
    return this.http.get<any>(`${this.url}/excel/${caso_id}`);
  }

  updateVersion(version: IVersions): Observable<any> {
    return this.http.put(`${this.url}/version`, version);
  }

  saveVersion(version: IVersions): Observable<any> {
    return this.http.post(`${this.url}/version`, version);
  }

  getVersions(caso_id: string): Observable<any> {
    return this.http.get<IVersions[]>(`${this.url}/version/${caso_id}`);
  }

  retenido(caso: string, cuenta: string, monto: number) {
    return this.http.post(`${this.url_cuentas}/retenido`, {
      caso,
      cuenta,
      monto,
    });
  }

  updateTransferencia(transaccion) {
    return this.http.put(`${this.url}/transaccion`, { transaccion });
  }

  deleteTransaction(transaccion_id) {
    return this.http.delete(`${this.url}/transaccion/${transaccion_id}`);
  }

  getTransaction(transaccion_id) {
    return this.http.get(`${this.url}/transaccionInfo/${transaccion_id}`);
  }

  getCuenta(id) {
    return this.http.get(`${this.url}/cuenta/${id}`);
  }

  cnode(node: INode): Observable<any> {
    return this.http.post(`${this.url}/node`, node);
  }

  dnode(id: string): Observable<any> {
    return this.http.delete(`${this.url}/node/${id}`);
  }

  cedge(edge: IEdge): Observable<any> {
    return this.http.post(`${this.url}/edge`, edge);
  }

  dedge(id: string): Observable<any> {
    return this.http.delete(`${this.url}/edge/${id}`);
  }

  bulkDeleteNodes(ids: string[]): Observable<any> {
    return this.http.post(`${this.url}/nodes/bulk-delete`, { ids });
  }

  bulkUpdateMontos(
    updates: Array<{
      _id: string;
      monto_recibido?: number;
      monto_retenido?: number;
      monto_ejecutado?: number;
      monto_salvado?: number;
      monto_pendiente?: number;
    }>
  ): Observable<any> {
    return this.http.put(`${this.url}/transacciones/bulk-montos`, { updates });
  }

  savePositions(positions: any[]) {
    return this.http.post(`${this.url}/saveCords`, { cords: positions });
  }

  /** Consolida en skuld el grafo inicial de la alerta (idempotente). */
  constructFirstNodes(
    alertInfo: IAlert,
    caso_id: string,
    numero_de_identificacion: string
  ): Observable<any> {
    return this.http.post(`${this.url}/constructFirstNodes`, {
      alertInfo,
      caso_id,
      numero_de_identificacion,
    });
  }
}
